export const THEME_CUSTOMIZABLE_TOKENS = ['actionPrimary', 'actionPrimaryHover', 'actionPrimarySoft'] as const
export type ThemeTokenKey = typeof THEME_CUSTOMIZABLE_TOKENS[number]

export type ThemePatch = Readonly<Partial<Record<ThemeTokenKey, string>>>
export type ValidatedTheme = Readonly<{ version: 1; tokens: ThemePatch }>

const PROTECTED_SEMANTIC_TOKENS = new Set(['success', 'warning', 'risk', 'info', 'ai'])
const HEX_COLOR = /^#[0-9a-f]{6}$/i

function channel(value: string): number {
  const normalized = Number.parseInt(value, 16) / 255
  return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4
}

export function contrastRatio(foreground: string, background: string): number {
  if (!HEX_COLOR.test(foreground) || !HEX_COLOR.test(background)) return 0
  const luminance = (hex: string) => {
    const value = hex.slice(1)
    const r = channel(value.slice(0, 2))
    const g = channel(value.slice(2, 4))
    const b = channel(value.slice(4, 6))
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }
  const a = luminance(foreground)
  const b = luminance(background)
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

export function validateThemePatch(input: Record<string, unknown>): ValidatedTheme {
  for (const key of Object.keys(input)) {
    if (PROTECTED_SEMANTIC_TOKENS.has(key)) throw new Error(`Protected semantic token cannot be customized: ${key}`)
    if (!THEME_CUSTOMIZABLE_TOKENS.includes(key as ThemeTokenKey)) throw new Error(`Unsupported theme token: ${key}`)
  }

  const tokens: Partial<Record<ThemeTokenKey, string>> = {}
  for (const key of THEME_CUSTOMIZABLE_TOKENS) {
    const value = input[key]
    if (value === undefined) continue
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) throw new Error(`Theme token ${key} must be a 6-digit hex color`)
    tokens[key] = value.toLowerCase()
  }

  if (tokens.actionPrimary && contrastRatio(tokens.actionPrimary, '#ffffff') < 4.5) {
    throw new Error('actionPrimary must keep WCAG AA contrast against inverse text')
  }

  return { version: 1, tokens }
}

export const THEME_CSS_VARIABLES: Readonly<Record<ThemeTokenKey, `--${string}`>> = {
  actionPrimary: '--color-action-primary',
  actionPrimaryHover: '--color-action-primary-hover',
  actionPrimarySoft: '--color-action-primary-soft',
}
